'use client'
import React, {useCallback, useEffect, useState} from 'react'

import TocItem from '@/app/[locale]/(docs)/components/DocsTocItem'
import {TreeNode} from '@/sanity/utils/treeNodes'

const flattenHeadings = (elements: TreeNode[]): TreeNode[] =>
    elements.flatMap((el) => [el, ...(el.children ? flattenHeadings(el.children) : [])])

export function RenderToc({elements}: {elements: TreeNode[]}) {
    const [activeId, setActiveId] = useState<string | null>(null)
    const [manualOverride, setManualOverride] = useState<boolean>(false)

    const handleClick = useCallback((slug: string) => {
        setActiveId(slug)
        setManualOverride(true)
        setTimeout(() => setManualOverride(false), 1000)
    }, [])

    useEffect(() => {
        const flatHeadings = flattenHeadings(elements)
        const headingElements = flatHeadings
            .map((el) => document.getElementById(el.slug))
            .filter(Boolean) as HTMLElement[]

        let lastActiveId: string | null = null

        const observerCallback = (entries: IntersectionObserverEntry[]) => {
            if (manualOverride) return

            const visibleHeadings = entries.filter((entry) => entry.isIntersecting)

            if (visibleHeadings.length > 0) {
                const topHeading = visibleHeadings.sort(
                    (a, b) => a.boundingClientRect.top - b.boundingClientRect.top,
                )[0]
                const newActiveId = topHeading.target.id

                if (newActiveId !== lastActiveId) {
                    lastActiveId = newActiveId
                    setActiveId(newActiveId)
                }
            }
        }

        const observer = new IntersectionObserver(observerCallback, {
            threshold: 0.3,
            rootMargin: '-20% 0px -70% 0px',
        })

        headingElements.forEach((el) => observer.observe(el))

        return () => observer.disconnect()
    }, [elements, manualOverride])

    return (
        <ul className="flex flex-col space-y-2 text-xs border-l-2 border-border-tertiary">
            {elements.map((el) => (
                <TocItem
                    key={el.slug}
                    element={el}
                    activeId={activeId}
                    handleClick={handleClick}
                />
            ))}
        </ul>
    )
}
